import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import { useAIPreferences, getAnalyzingSteps } from '@/contexts/AIPreferencesContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  fetchGenerationHistoryResume,
  generateAIContent,
  type GenerationHistoryResume,
} from '@/lib/ai';
import { saveAIResult } from '@/lib/aiSession';
import { getStudyScope, isStudyScopeCurrent, writeGenerationSession } from '@/lib/studyStorage';
import { fetchGenerationAccess } from '@/lib/generationAccess';
import { analysisRequestSignature } from '@/lib/generation-polling';
import { recordScan, updateScanProgress } from '@/mocks/scanHistory';
import type { ServerGenerationAccess } from '@/lib/generationAccess';
import GenerationAdvertisement from './GenerationAdvertisement';
import AccountRequiredGate from '@/components/feature/AccountRequiredGate';

const dotPhases = ['', '.', '..', '...'];
const GENERATION_JOB_KEY = 'lavyn_generation_job';

export default function ScanAnalyzing() {
  const navigate = useNavigate();
  const location = useLocation();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const { preferences } = useAIPreferences();
  const { user, isRehydrated } = useAuth();

  const [outputMode, setOutputMode] = useState<'text' | 'video'>('text');
  const [accountRequired, setAccountRequired] = useState(false);
  const [dotIndex, setDotIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);
  const [pulsing, setPulsing] = useState(false);
  const [complete, setComplete] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [requestStatus, setRequestStatus] = useState<'pending' | 'complete' | 'failed'>('pending');
  const [isResuming, setIsResuming] = useState(false);
  const [generationAccess, setGenerationAccess] = useState<ServerGenerationAccess | null>(null);
  const [retryWithNewRequestId, setRetryWithNewRequestId] = useState(false);
  const [queueStatus, setQueueStatus] = useState<{ status: string; position: number } | null>(null);
  const [historyResume, setHistoryResume] = useState<GenerationHistoryResume | null>(null);
  const requestStarted = useRef(false);
  const resumeLookupStarted = useRef('');
  const mountedRef = useRef(false);
  const receivedStatusRef = useRef(false);
  const progressRef = useRef(0);
  const activeHistoryId = useRef('');
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Dynamic steps based on AI learning mode preference
  const steps = getAnalyzingSteps(preferences.learningMode, outputMode);

  const t = {
    pageBg: isDark ? 'bg-[#0F0F10]' : 'bg-white',
    textPrimary: isDark ? 'text-white' : 'text-neutral-900',
    textMuted: isDark ? 'text-neutral-500' : 'text-neutral-400',
    textSecondary: isDark ? 'text-neutral-400' : 'text-neutral-500',
    textFaint: isDark ? 'text-neutral-700' : 'text-neutral-300',
    progressTrack: isDark ? 'bg-neutral-800' : 'bg-neutral-200',
    iconBg: isDark ? 'bg-neutral-900 border-neutral-800' : 'bg-neutral-100 border-neutral-200',
    dotColor: isDark ? 'bg-neutral-600' : 'bg-neutral-400',
    dotPrimary: isDark ? 'bg-white' : 'bg-neutral-900',
    percentage: isDark ? 'text-neutral-600' : 'text-neutral-400',
    gridOpacity: 'opacity-[0.03]',
  };

  // Read output mode
  useEffect(() => {
    const mode = sessionStorage.getItem('scan_output_mode') as 'text' | 'video';
    if (mode === 'video' || mode === 'text') setOutputMode(mode);
  }, []);

  useEffect(() => {
    const generationId = new URLSearchParams(location.search).get('resume') || '';
    if (!generationId || !isRehydrated || !user ||
        historyResume?.generationId === generationId || resumeLookupStarted.current === generationId) return;
    resumeLookupStarted.current = generationId;
    setOutputMode('video');
    void fetchGenerationHistoryResume(generationId).then(async resume => {
      if (!mountedRef.current) return;
      if (resume.status === 'completed') {
        const owner = getStudyScope();
        await saveAIResult(resume.result, owner);
        recordScan({
          generationId: resume.generationId,
          type: 'video',
          title: resume.result.title,
          subject: resume.result.subject,
          uploadType: 'Image',
          status: 'completed',
          progress: 100,
          ...(resume.result.generationMetrics?.progressiveDelivery === 'full_audio_fallback'
            ? { deliveryMode: 'full_audio_fallback' as const }
            : resume.result.generationMetrics?.progressiveDelivery === 'progressive'
              ? { deliveryMode: 'progressive' as const }
              : {}),
        }, owner);
        navigate(`/scan/result-video?generation=${encodeURIComponent(resume.generationId)}`, { replace: true });
        return;
      }
      setHistoryResume(resume);
      setIsResuming(true);
    }).catch(error => {
      if (!mountedRef.current) return;
      requestStarted.current = true;
      setRequestStatus('failed');
      setRequestError(error instanceof Error ? error.message : 'This saved lesson could not be resumed.');
    });
  }, [historyResume, isRehydrated, location.search, navigate, user]);

  // Flag that user has generated content and should see save modal
  useEffect(() => {
    sessionStorage.setItem('scan_awaiting_save', 'true');
  }, []);

  useEffect(() => {
    if (!isRehydrated || requestStarted.current) return;
    const resumeGenerationId = new URLSearchParams(location.search).get('resume') || '';
    if (resumeGenerationId &&
        (historyResume?.status !== 'incomplete' || historyResume.generationId !== resumeGenerationId)) return;
    const resumeInput = historyResume?.status === 'incomplete' ? historyResume.resumeInput : null;
    const outputFormat: 'text' | 'video' = resumeInput
      ? 'video'
      : sessionStorage.getItem('scan_output_mode') === 'text' ? 'text' : 'video';
    if (outputFormat === 'video' && !user) {
      requestStarted.current = true;
      setRequestStatus('failed');
      setAccountRequired(true);
      return;
    }
    requestStarted.current = true;
    // Do not guess Free/Premium during auth rehydration: fetch verified ad eligibility.
    void fetchGenerationAccess().then(access => {
      if (mountedRef.current && !receivedStatusRef.current) setGenerationAccess(access);
    }).catch(() => { /* The generation API reports authoritative access errors. */ });
    const context = resumeInput ? {
      subject: resumeInput.subject,
      grade: resumeInput.grade,
      topic: resumeInput.topic,
      difficulty: resumeInput.difficulty,
      ...(resumeInput.learningMode ? { fromHomeComposer: true, homeLearningMode: resumeInput.learningMode } : {}),
    } : JSON.parse(sessionStorage.getItem('scan_context') || '{}');
    const image = resumeInput?.image ?? sessionStorage.getItem('scan_input_image') ?? '';
    const instruction = resumeInput?.instruction ?? sessionStorage.getItem('scan_instruction') ?? '';
    const teachingMode = resumeInput?.teachingMode ?? (sessionStorage.getItem('scan_help_mode') === 'answer' ? 'answer' : 'explain');
    const learningMode: 'effective' | 'step-by-step' | undefined = resumeInput?.learningMode ?? (context.fromHomeComposer
      ? (context.homeLearningMode === 'step-by-step' ? 'step-by-step' : 'effective') : undefined);
    const signature = analysisRequestSignature({
      mode: image ? 'scan' : 'topic',
      subject: context.subject,
      grade: context.grade,
      topic: context.topic,
      difficulty: context.difficulty,
      instruction,
      teachingMode,
      ...(learningMode ? { learningMode } : {}),
      outputFormat,
      image,
    });
    let requestId = resumeInput && historyResume?.status === 'incomplete' ? historyResume.requestId : '';
    try {
      const savedJob = requestId ? {} as {
        requestId?: string;
        signature?: string;
      } : JSON.parse(sessionStorage.getItem(GENERATION_JOB_KEY) || '{}') as {
        requestId?: string;
        signature?: string;
      };
      if (savedJob.requestId && savedJob.signature === signature) {
        requestId = savedJob.requestId;
        setIsResuming(true);
      }
    } catch {
      sessionStorage.removeItem(GENERATION_JOB_KEY);
    }
    if (!requestId) {
      requestId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `lesson-${Date.now()}`;
      sessionStorage.setItem(GENERATION_JOB_KEY, JSON.stringify({ requestId, signature }));
    }
    const owner = getStudyScope();
    if (resumeInput) {
      writeGenerationSession(GENERATION_JOB_KEY, JSON.stringify({ requestId, signature }), owner);
    }
    const historyId = `ai-${requestId}`;
    activeHistoryId.current = historyId;
    const historyBase = {
      generationId: historyId,
      type: outputFormat,
      subject: context.subject || 'General Studies',
      uploadType: 'Image' as const,
    };
    recordScan({ ...historyBase, title: context.topic || instruction || 'Your lesson', status: 'processing', progress: 0 });
    generateAIContent({
      requestId,
      mode: image ? 'scan' : 'topic',
      subject: context.subject,
      grade: context.grade,
      topic: context.topic,
      difficulty: context.difficulty,
      image,
      instruction,
      teachingMode,
      ...(resumeInput?.presentationReference ? { presentationReference: resumeInput.presentationReference } : {}),
      ...(learningMode ? { learningMode, outputFormat } : {}),
    }, (access, queue) => {
      receivedStatusRef.current = true;
      if (!isStudyScopeCurrent(owner)) return;
      if (mountedRef.current) {
        setGenerationAccess(access);
        setQueueStatus(queue || null);
      }
    }).then(async (result) => {
      if (!owner.ownerId && (!mountedRef.current || !isStudyScopeCurrent(owner))) return;
      await saveAIResult(result, owner);
      recordScan({
        ...historyBase,
        generationId: result.id,
        title: result.title,
        subject: result.subject,
        status: 'completed',
        progress: 100,
      }, owner);
      if (!isStudyScopeCurrent(owner)) return;
      sessionStorage.removeItem(GENERATION_JOB_KEY);
      sessionStorage.setItem('ai_request_complete', 'true');
      sessionStorage.removeItem('ai_request_failed');
      setProgress(100);
      setRequestStatus('complete');
    }).catch((error) => {
      const errorCode = typeof (error as { code?: unknown })?.code === 'string'
        ? (error as { code: string }).code : '';
      const incomplete = outputFormat === 'video' && (
        (error as { terminal?: unknown })?.terminal !== true ||
        ['GENERATION_OUTCOME_UNCERTAIN', 'GENERATION_ACCESS_UNAVAILABLE', 'GENERATION_RESUME_UNAVAILABLE'].includes(errorCode)
      );
      recordScan({
        ...historyBase,
        title: context.topic || instruction || 'Your lesson',
        status: incomplete ? 'incomplete' : 'failed',
      }, owner);
      if (!isStudyScopeCurrent(owner)) return;
      console.error('[LAVYN AI] Lesson generation failed in the browser:', error);
      setRequestError(error instanceof Error ? error.message : 'Lesson generation failed without an error response.');
      if ((error as Error & { retryWithNewRequestId?: boolean }).retryWithNewRequestId) {
        setRetryWithNewRequestId(true);
      }
      sessionStorage.setItem('ai_request_failed', 'true');
      setRequestStatus('failed');
    });
  }, [historyResume, isRehydrated, location.search, user]);

  // Dot animation
  useEffect(() => {
    const dotInterval = setInterval(() => {
      setDotIndex((i) => (i + 1) % dotPhases.length);
    }, 500);
    return () => clearInterval(dotInterval);
  }, []);

  // Progress
  useEffect(() => {
    if (requestStatus !== 'pending') return;
    const progressInterval = setInterval(() => {
      setProgress((p) => {
        if (p >= 92) return 92;
        const next = Math.min(p + Math.random() * 8 + 2, 92);
        progressRef.current = next;
        return next;
      });
    }, 650);
    return () => clearInterval(progressInterval);
  }, [requestStatus]);

  useEffect(() => {
    if (requestStatus === 'pending' && activeHistoryId.current) {
      updateScanProgress(activeHistoryId.current, progress);
    }
  }, [progress, requestStatus]);

  // Step index tracking
  useEffect(() => {
    const stepCount = steps.length;
    const idx = Math.min(
      Math.floor((progress / 100) * stepCount),
      stepCount - 1
    );
    setStepIndex(idx);
    if (progress >= 95) setPulsing(true);
    if (progress >= 100 && !complete && requestStatus === 'complete') {
      setComplete(true);
      setTimeout(() => {
        const target = outputMode === 'video' ? '/scan/result-video' : '/scan/result-text';
        navigate(target, { replace: true });
      }, 800);
    }
  }, [progress, steps.length, complete, outputMode, navigate, requestStatus]);

  const clamped = Math.min(progress, 100);
  const currentStep = steps[stepIndex] || steps[0];

  if (accountRequired) {
    return (
      <AccountRequiredGate
        variant="page"
        isDark={isDark}
        onClose={() => navigate('/', { replace: true })}
      />
    );
  }

  return (
    <div className={`flex flex-col min-h-full ${t.pageBg} items-center justify-center px-6 relative transition-colors duration-500 overflow-hidden`}>
      {/* Animated Grid Background */}
      <div
        className={`absolute inset-0 ${t.gridOpacity}`}
        style={{
          backgroundImage:
            'linear-gradient(rgba(128,128,128,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(128,128,128,0.5) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      {/* Ambient glow */}
      <motion.div
        className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full"
        style={{ background: isDark ? 'radial-gradient(circle, rgba(255,255,255,0.03) 0%, transparent 70%)' : 'radial-gradient(circle, rgba(0,0,0,0.02) 0%, transparent 70%)' }}
        animate={{ scale: [1, 1.15, 1], opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      />

      <motion.div
        className="relative z-10 flex flex-col items-center text-center max-w-sm"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
      >
        {/* AI Orb Animation */}
        <div className="relative mb-12">
          {/* Outer ring */}
          <motion.div
            className="absolute inset-0 rounded-full"
            style={{ border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}` }}
            animate={{ rotate: 360 }}
            transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
          >
            <div className={`absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1 w-2 h-2 rounded-full ${t.dotPrimary}`} style={{ opacity: 0.4 }} />
          </motion.div>

          {/* Middle ring */}
          <motion.div
            className="absolute -inset-4 rounded-full"
            style={{ border: `1px dashed ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}` }}
            animate={{ rotate: -360 }}
            transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
          />

          {/* Core orb */}
          <motion.div
            className={`w-24 h-24 rounded-full ${t.iconBg} border flex items-center justify-center relative`}
            animate={pulsing ? { scale: [1, 1.06, 1] } : { scale: [1, 1.02, 1] }}
            transition={pulsing ? { duration: 0.6, repeat: Infinity } : { duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
          >
            <div className="w-12 h-12 flex items-center justify-center">
              <i className={`${outputMode === 'video' ? 'ri-movie-ai-line' : 'ri-brain-line'} ${t.textPrimary} text-3xl`} />
            </div>
            {/* Inner glow */}
            <motion.div
              className="absolute inset-0 rounded-full"
              style={{ boxShadow: isDark ? 'inset 0 0 20px rgba(255,255,255,0.03)' : 'inset 0 0 20px rgba(0,0,0,0.02)' }}
              animate={{ opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            />
          </motion.div>

          {/* Orbiting particles */}
          <motion.div
            className="absolute inset-0"
            animate={{ rotate: 360 }}
            transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
          >
            <div className={`absolute top-0 left-1/2 -translate-x-1/2 -translate-y-3 w-1.5 h-1.5 rounded-full ${t.dotPrimary}`} />
          </motion.div>
          <motion.div
            className="absolute inset-0"
            animate={{ rotate: -360 }}
            transition={{ duration: 9, repeat: Infinity, ease: 'linear' }}
          >
            <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-3 w-1 h-1 rounded-full ${t.dotColor}`} />
          </motion.div>
        </div>

        {/* Mode Badge */}
        <div className={`mb-3 px-3 py-1 rounded-full border ${isDark ? 'border-white/[0.06] bg-white/[0.03]' : 'border-neutral-200 bg-neutral-50'}`}>
          <span className={`text-[11px] font-medium ${t.textMuted}`}>
            {preferences.learningMode} &bull; {outputMode === 'video' ? 'Video' : 'Text'}
          </span>
        </div>

        {/* Status Text */}
        <AnimatePresence mode="wait">
          <motion.div
            key={stepIndex}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
          >
            <motion.h2
              className={`${t.textPrimary} text-lg font-bold mb-1.5`}
              animate={{ opacity: [0.85, 1, 0.85] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            >
              {currentStep.message}{dotPhases[dotIndex]}
            </motion.h2>
            <p className={`${t.textMuted} text-sm mb-10`}>
              {requestError || (isResuming ? 'Resuming your saved generation from where it paused…' : currentStep.detail)}
            </p>
          </motion.div>
        </AnimatePresence>
        {/* Circular Progress */}
        <div className="relative w-20 h-20 mb-6">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
            <path
              className={t.progressTrack}
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none"
              strokeWidth="2"
              stroke="currentColor"
            />
            <motion.path
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none"
              stroke={isDark ? '#fff' : '#171717'}
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray={`${clamped}, 100`}
              transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <AnimatePresence mode="wait">
              {complete ? (
                <motion.div
                  key="check"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                  className="w-6 h-6 flex items-center justify-center"
                >
                  <i className="ri-check-line text-emerald-500 text-xl" />
                </motion.div>
              ) : (
                <motion.span
                  key="pct"
                  className={`text-xs font-semibold ${t.textSecondary}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {Math.round(clamped)}%
                </motion.span>
              )}
            </AnimatePresence>
          </div>
        </div>

        {requestStatus === 'pending' && (
          <>
            {queueStatus?.status === 'queued' && (
              <p className={`mt-3 text-xs ${t.textSecondary}`} role="status">
                Waiting for generation · queue position {queueStatus.position}
              </p>
            )}
            <GenerationAdvertisement access={generationAccess} isDark={isDark} />
          </>
        )}

        {/* Error actions */}
        <AnimatePresence>
          {requestStatus === 'failed' && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2 flex flex-col items-center gap-3"
            >
              <button
                type="button"
                onClick={() => {
                  if (retryWithNewRequestId) sessionStorage.removeItem(GENERATION_JOB_KEY);
                  navigate('/scan/output', { replace: true });
                }}
                className="rounded-xl bg-neutral-900 px-5 py-2.5 text-xs font-semibold text-white"
              >
                Try Again
              </button>
              <button
                type="button"
                onClick={() => navigate('/scan/start', { replace: true })}
                className={`text-xs font-medium ${t.textSecondary}`}
              >
                Scan a different question
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Completion indicator */}
        <AnimatePresence>
          {complete && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2"
            >
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className={`text-xs font-medium ${t.textSecondary}`}>
                {outputMode === 'video' ? 'Video ready' : 'Analysis complete'}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}